import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import useSetProposalContentSection from '@/api/mutations/proposals/useSetProposalContentSection';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import {
  SetProposalContentSectionInput,
  SetProposalContentSectionMutation,
} from '@/gql/graphql';

import { applyProposalContentSection } from './applyProposalContentSection';
import { createOptimisticProposalMutationOptions } from './createOptimisticProposalMutation';

/**
 * Optimistic wrapper around {@link useSetProposalContentSection}: patches the
 * section's content in the cached proposal in `onMutate` so cache readers (the
 * preview pane) see the edit immediately, then rolls back on error. The base
 * hook invalidates the proposal on success, reconciling with server truth. The
 * content editor itself seeds once from the cache, so this write cannot reset
 * it mid-typing.
 */
const useOptimisticSetProposalContentSection = (args: {
  proposalId: string;
  options?: UseMutationOptions<
    SetProposalContentSectionMutation,
    unknown,
    SetProposalContentSectionInput
  >;
}) => {
  const { proposalId, options } = args;
  const queryClient = useQueryClient();
  const queryKey = proposalKeys.proposal(proposalId);

  return useSetProposalContentSection({
    proposalId,
    options: {
      ...options,
      ...createOptimisticProposalMutationOptions({
        queryClient,
        queryKey,
        apply: applyProposalContentSection,
        options,
      }),
    },
  });
};

export default useOptimisticSetProposalContentSection;
